import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';

import { tableHelper } from '@builttocreate/engine-utils';

import TableEditorHeader from './TableEditorHeader';
import TableEditorRow from './TableEditorRow';
import TableEditorRte from './TableEditorRte';
import PopoverMenu from '../../misc/PopoverMenu';
import Icon from '../../misc/Icon';
import Button from '../../misc/Button';

import FieldTableCustomColumnIds from '../../../constants/FieldTableCustomColumnIds';
import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';
import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

const {
  getRows,
  sortRows,
  getColumns,
  sortColumns,
  getColumnOrder,
} = tableHelper;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items:baseline;
  overflow: auto;
`;

const StyledTable = styled.div`
  display: flex;
  overflow: auto;
  flex-direction: column;
`;

const StyledRowsWrapper = styled.div`
  display: flex;
  max-height: 200px;
  overflow-y: auto;
  flex-direction: column;
`;

const StyledFooter = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  margin: 8px;
  margin-left: 26px;
  flex-wrap: wrap;
  width: 600px;
`;

const StyledAddIcon = styled.span`
  &:hover {
    cursor: pointer;
  }
`;

class TableEditor extends PureComponent {

  static contextType = ThemeContext

  static propTypes = {

    id: PropTypes.string,
    fieldId: PropTypes.string, //fieldPosition Id
    focused: PropTypes.string,

    activeFieldPosition: PropTypes.object,

    onRowCreate: PropTypes.func,
    onColumnCreate: PropTypes.func,
    onColumnEdit: PropTypes.func,
    onColumnMoveLeft: PropTypes.func,
    onColumnMoveRight: PropTypes.func,

    displayType: PropTypes.string,
    onRteSettingsCreate: PropTypes.func,
    onRteSettingsUpdate: PropTypes.func,
    onRteSettingsMoveLeft: PropTypes.func,
    onRteSettingsMoveRight: PropTypes.func,
    onRteSettingsDelete: PropTypes.func,

  }

  constructor(props) {
    super(props);
    this.state = {
      rows: [],
      columns: [],
      displayAddColumnPopover: false
    };
  }

  componentDidMount() {
    this.populateTableData();
  }

  componentDidUpdate(prevProps) {

    const { activeFieldPosition } = this.props;

    if (activeFieldPosition && activeFieldPosition !== prevProps.activeFieldPosition) {
      this.populateTableData();
    }

  }

  populateTableData = () => {

    const { activeFieldPosition } = this.props;
    const { value, rowOrder, tableColumns, tableColumnOrder, rowDecorators } = activeFieldPosition || {};

    const formattedRows = this.getFormattedAndSortedRows(value, rowOrder);
    const formattedColumns = this.getFormattedAndSortedColumns(tableColumns, tableColumnOrder, rowDecorators);

    this.setState({ rows: formattedRows, columns: formattedColumns });

  }

  render() {

    const { columns, rows, displayAddColumnPopover } = this.state;
    const { id, activeFieldPosition, focused, disableDragAndDrop, fieldSettings, identifier, type, displayType } = this.props;

    if (displayType === FieldDisplayTypes.rte) {
      return (
        <TableEditorRte
          id={id}
          fieldId={this.props.fieldId}
          activeFieldPosition={activeFieldPosition}
          onRteSettingsCreate={this.props.onRteSettingsCreate}
          onRteSettingsUpdate={this.props.onRteSettingsUpdate}
          onRteSettingsMoveLeft={this.props.onRteSettingsMoveLeft}
          onRteSettingsMoveRight={this.props.onRteSettingsMoveRight}
          onRteSettingsDelete={this.props.onRteSettingsDelete}
        />
      );
    }

    const fieldSettingsConfig = fieldSettings?.field;
    const columnSettingsConfig = fieldSettingsConfig?.tableColumns;

    const items = [
      {
        text: 'Row',
        icon: { icon: ThemeIcons.misc.add, size: 'sm' },
        onClick: () => {
          this.handleRowCreate();
          this.setState({ displayAddColumnPopover: false });
        }
      },
      {
        text: 'Text Column',
        type: 'text',
        icon: { icon: ThemeIcons.fields.text, size: 'sm' },
        onClick: () => {
          this.handleColumnCreate(FieldTableColumnTypes.text);
          this.setState({ displayAddColumnPopover: false });
        }
      },
      {
        text: 'Dropdown Column',
        type: 'dropdown',
        icon: { icon: ThemeIcons.fields.dropdown, size: 'sm' },
        onClick: () => {
          this.handleColumnCreate(FieldTableColumnTypes.dropdown);
          this.setState({ displayAddColumnPopover: false });
        }
      },
      {
        text: 'Selection Column',
        type: 'multiSelect',
        icon: { icon: ThemeIcons.fields.multiSelect, size: 'sm' },
        onClick: () => {
          this.handleColumnCreate(FieldTableColumnTypes.multiSelect);
          this.setState({ displayAddColumnPopover: false });
        }
      },
      {
        text: 'Image Column',
        type: 'image',
        icon: { icon: ThemeIcons.fields.image, size: 'sm' },
        onClick: () => {
          this.handleColumnCreate(FieldTableColumnTypes.image);
          this.setState({ displayAddColumnPopover: false });
        }
      },
      {
        text: 'Number Column',
        type: 'number',
        icon: { icon: ThemeIcons.fields.number, size: 'sm' },
        onClick: () => {
          this.handleColumnCreate(FieldTableColumnTypes.number);
          this.setState({ displayAddColumnPopover: false });
        }
      },
      {
        text: 'Date Time Column',
        type: 'date',
        icon: { icon: ThemeIcons.fields.date, size: 'sm' },
        onClick: () => {
          this.handleColumnCreate(FieldTableColumnTypes.date);
          this.setState({ displayAddColumnPopover: false });
        }
      },
      {
        text: 'Label Column',
        type: 'block',
        icon: { icon: ThemeIcons.fields.blockParagraph, size: 'sm' },
        onClick: () => {
          this.handleColumnCreate(FieldTableColumnTypes.block);
          this.setState({ displayAddColumnPopover: false });
        }
      },
      {
        text: "Barcode Column",
        type: 'barcode',
        icon: { icon: ThemeIcons.fields.barcode, size: 'sm' },
        onClick: () => {
          this.handleColumnCreate(FieldTableColumnTypes.barcode);
          this.setState({ displayAddColumnPopover: false });
        }
      },
      {
        text: 'Signature Column',
        type: 'signature',
        icon: { icon: ThemeIcons.fields.signature, size: 'sm' },
        onClick: () => {
          this.handleColumnCreate(FieldTableColumnTypes.signature);
          this.setState({ displayAddColumnPopover: false });
        }
      },
    ];

    const { types } = columnSettingsConfig || []

    const filteredItems = types && types.length > 0 ? items.filter((item) => !item.type || types.includes(item.type))
      : items.filter((item) => !item.type || item.type === 'text' || item.type === 'dropdown' || item.type === 'image');

    return (
      <StyledWrapper>
        <StyledTable data-testid={`${id}-table-editor-table`}>
          <TableEditorHeader
            id={id}
            fieldPositionColumns={activeFieldPosition.fieldPositionTableColumns}
            columns={columns}
            onEdit={this.handleColumnEdit}
            onMoveLeft={this.handleColumnMoveLeft}
            onMoveRight={this.handleColumnMoveRight}
          />
          <StyledRowsWrapper>
            {rows.map((row, rowIndex) => (
              <div key={`${row._id}-${rowIndex}`} id="table-editor-row">
                <TableEditorRow
                  id={id}
                  activeFieldPosition={activeFieldPosition}
                  key={`${row._id}-${rowIndex}`}
                  focused={focused}
                  row={row}
                  rowIndex={rowIndex}
                  columns={columns}
                  rowDecorators={activeFieldPosition?.rowDecorators}
                  disableDragAndDrop={disableDragAndDrop}
                  onDelete={rows.length < 2 ? null : this.handleRowDelete}
                />
              </div>
            ))}
          </StyledRowsWrapper>
          <StyledFooter>
            <PopoverMenu
              reposition={false}
              isOpen={displayAddColumnPopover}
              positions={['top']}
              padding={18}
              onClickOutside={() => this.setState({ displayAddColumnPopover: false })}
              items={filteredItems}
            >
              <StyledAddIcon>
                <Button
                  icon={ThemeIcons.misc.add}
                  onClick={(e) => {
                    if (e) e.stopPropagation();
                    this.setState({ displayAddColumnPopover: !displayAddColumnPopover });
                  }}
                />
              </StyledAddIcon>
            </PopoverMenu>
          </StyledFooter>
        </StyledTable>
      </StyledWrapper>
    );

  }

  handleRowCreate = () => {

    const { onRowCreate, fieldId } = this.props;
    onRowCreate(fieldId);

  }

  handleRowDelete = (rowId, rowIndex) => {

    const { onRowDelete, fieldId } = this.props;
    onRowDelete(fieldId, rowId, rowIndex);

  }

  handleColumnCreate = (type) => {

    const { onColumnCreate, fieldId } = this.props;
    onColumnCreate(fieldId, type);

  }

  handleColumnEdit = (columnId) => {

    const { onColumnEdit, fieldId } = this.props;
    onColumnEdit(fieldId, columnId);

  }

  handleColumnMoveRight = (columnId) => {

    const { onColumnMoveRight, fieldId } = this.props;
    onColumnMoveRight(fieldId, columnId);

  }

  handleColumnMoveLeft = (columnId) => {

    const { onColumnMoveLeft, fieldId } = this.props;
    onColumnMoveLeft(fieldId, columnId);

  }

  getFormattedAndSortedColumns = (tableColumns, tableColumnOrder, rowDecorators) => {
    if (!tableColumns) return [];

    const columnOrder = getColumnOrder(tableColumnOrder, tableColumns);
    const nextColumns = getColumns(sortColumns(tableColumns, columnOrder));

    //Add row number
    if (nextColumns[0] && nextColumns[0]._id !== FieldTableCustomColumnIds.rowNumber) {
      nextColumns.unshift({
        _id: FieldTableCustomColumnIds.rowNumber,
        type: FieldTableCustomColumnIds.rowNumber,
        width: 24,
      });
    }

    //Add row decorators column (after rowNumber, before data columns)
    const activeRowDecorators = rowDecorators ? rowDecorators.filter((d) => !d.deleted) : [];
    const hasRowDecoratorsColumn = nextColumns.some((c) => c._id === FieldTableCustomColumnIds.rowDecorators);
    if (activeRowDecorators.length > 0 && !hasRowDecoratorsColumn) {
      const isSingleIconOnly = activeRowDecorators.length === 1 && !!activeRowDecorators[0].icon && !activeRowDecorators[0].label;
      const decoratorColumnWidth = isSingleIconOnly ? 36 : 80;
      const rowNumberIndex = nextColumns.findIndex((c) => c._id === FieldTableCustomColumnIds.rowNumber);
      nextColumns.splice(rowNumberIndex + 1, 0, {
        _id: FieldTableCustomColumnIds.rowDecorators,
        type: FieldTableCustomColumnIds.rowDecorators,
        width: decoratorColumnWidth,
      });
    }

    //Add row settings
    if (nextColumns[0] && nextColumns[0]._id !== FieldTableCustomColumnIds.rowMenu) {
      nextColumns.unshift({
        _id: FieldTableCustomColumnIds.rowMenu,
        type: FieldTableCustomColumnIds.rowMenu,
        width: 24,
      });
    }

    return nextColumns;

  }

  getFormattedAndSortedRows = (rows, rowOrder) => {
    return sortRows(getRows(rows), rowOrder);
  }

}

export default TableEditor;
